/**
 * Detection helpers for the SSO surfaces that partners embed inside an
 * `<iframe>` or a native in-app WebView.
 *
 * The SSO handshake itself may run embedded (the legacy flow hands credentials
 * to the host via postMessage), but any navigation that leaves the SSO surface
 * for the full DUPR app must escape to a real top-level browser tab instead of
 * rendering the app inside the host's small embed. Use `isEmbeddedContext()`
 * to decide between an in-SPA navigation and a `target="_blank"` break-out link.
 */

/** True when this window is rendered inside another page's frame. */
export const isFramed = (): boolean => {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin access to window.top throws — that itself means we are framed.
    return true
  }
}

/**
 * Best-effort detection of native in-app WebViews (partner mobile apps and
 * social-network in-app browsers). These are top-level windows, so `isFramed`
 * cannot see them — the user agent is the only signal available to the page.
 */
export const isInAppWebView = (): boolean => {
  const ua = navigator.userAgent || ''
  // Android System WebView marks itself with a standalone `wv` token.
  if (/\bwv\b/.test(ua)) return true
  // Common in-app browsers identify themselves explicitly.
  if (/FBAN|FBAV|Instagram|Line\/|Snapchat|Twitter|LinkedIn|GSA\/|musical_ly|BytedanceWebview/i.test(ua)) return true
  // iOS WKWebView: real Safari (and iOS Chrome/Firefox) always append a
  // `Safari/` token; embedded WKWebViews do not.
  if (/iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua)) return true
  return false
}

/** True when the page runs inside a partner embed (iframe or in-app WebView). */
export const isEmbeddedContext = (): boolean => isFramed() || isInAppWebView()
